import type { QueryClient } from '@tanstack/react-query';
import { merge } from 'lodash-es';
import { nanoid } from 'nanoid';

import type { ErrorReporter } from '@change-corgi/core/errorReporter/common';
import type { UtilityContext } from '@change-corgi/core/react/utilityContext';

import type { Schema } from '../../shared/schema';

import { getFindOrCreateDraftData } from './api';
import { getDraftFromLocalStorage } from './getDraftFromLocalStorage';
import { getDraftFromQueryClient } from './getDraftFromQueryClient';

type UtilityErrorReporter = Pick<ErrorReporter, 'report'>;
type FindOrCreateDraftOptions = {
	errorReporter: UtilityErrorReporter;
	gqlClient: UtilityContext['gql'];
	queryClient?: QueryClient;
};

const RECOMMENDED_DRAFT_TYPE = 'recommendations_draft';

export async function findOrCreateDraft(id: string | null, options: FindOrCreateDraftOptions): Promise<Schema> {
	const defaultDraft: Schema = { id: nanoid() };

	let draft: Schema | null = null;
	try {
		const { experiments } = await getFindOrCreateDraftData(options.gqlClient);
		const recommendationVariant = experiments.starfishSap3RecommendedAngles.variation;
		const isRecommendationsVariant = !recommendationVariant.includes('control');
		draft = getDraftFromQueryClient(options.queryClient) ?? getDraftFromLocalStorage(id) ?? defaultDraft;

		// reset draft if it was created with recommendations experiment but experiment is off
		if (draft.type === RECOMMENDED_DRAFT_TYPE && !isRecommendationsVariant) {
			return defaultDraft;
		}

		const draftWithMetadata = (() => {
			if (recommendationVariant === 'variant') {
				return { metadata: { variant: 'recommendations' } };
			}

			if (recommendationVariant === 'variant_b') {
				return { metadata: { variant: 'recommendations_variant_b' } };
			}

			return { metadata: { variant: 'control' } };
		})();
		return merge({}, draft, draftWithMetadata);
	} catch (error) {
		void options.errorReporter.report(
			{
				error: new Error('Failed to find or create draft', { cause: error }),
				params: {
					draftId: id,
					operation: 'findOrCreateDraft',
					draft,
				},
			},
			{
				severity: 'error',
			},
		);

		// Always return at least a minimal draft
		return defaultDraft;
	}
}
